import { Card, CardContent, Flex, hooks, Icon, ProgressButton, Text } from '@bitrise/bitkit';
import { TypeMetricKey } from '@trace/types';
import React, { FunctionComponent, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import {
  METRIC_IS_COUNTER_BY_KEY,
  METRIC_NAME_BY_KEY,
  METRIC_UNIT_BY_KEY,
  METRIC_UNIT_NAME,
} from '../../../constants/Metrics';
import dataAtIds from '../../../dataAtIds.json';
import useSessions from '../../SessionViewer/useSessions';

import useTrackingEventTimed from '../../Tracking/useTrackingEventTimed';
import { formatter1 } from '../../../utils/metric/getFormattedValue';
import { trackEvent } from '../../../utils/tracking';
import AttributeSelector from '../../AttributeSelector/AttributeSelector';
import Filters from '../../Filters/Filters';
import FiltersSelector from '../../Filters/FiltersSelector';
import Page from '../../Page/Page';
import PageBody from '../../Page/PageBody';
import PageBreadcrumb from '../../Page/PageBreadcrumb';
import PageHeader from '../../Page/PageHeader';
import PageTitle from '../../Page/PageTitle';
import TimeRange from '../../TimeRange/TimeRange';
import { URLStateContext } from '../../URLState/URLState';
import LineChart from '../../ViewVisualisations/Percentile/LineChart/LineChart';
import StackedBarChart from '../../ViewVisualisations/Percentile/StackedBarChart/StackedBarChart';
import PerformanceDetailBreakdown from './PerformanceDetailBreakdown';
import usePercentileMetric from '../usePercentileMetric';

const { useMediaQuery } = hooks;

const Detail: FunctionComponent = () => {
  const { appId, metric } = useParams<{ appId: string; metric: TypeMetricKey }>();
  const { attributeGroups, push } = useContext(URLStateContext);
  const match = useMediaQuery(['1056px']);
  const { isFetching, total } = useSessions(null);
  const metricName = METRIC_NAME_BY_KEY[metric];
  const metricData = usePercentileMetric(metric);
  const handlePointerEnter = useTrackingEventTimed('Metric.Interaction', {
    attribute: attributeGroups[metric],
    metric,
  });

  useEffect(() => {
    trackEvent('PerformanceDetail.Visit', { metric });
  }, [metric]);

  return (
    <Page data-trace-at-id={dataAtIds.PerformanceDetailPage}>
      <PageHeader>
        <Flex alignChildrenVertical="end" direction="horizontal" gap="x4">
          <Flex grow initial="none">
            <PageBreadcrumb to={`/${appId}/appPerformance`}>Performance</PageBreadcrumb>

            <PageTitle>{metricName || metric}</PageTitle>
          </Flex>

          <Flex>
            <ProgressButton
              data-trace-at-id={dataAtIds.PerformanceDetailSessionButton}
              disabled={total === 0}
              level="secondary"
              onClick={() => push(`/${appId}/appPerformance/${metric}/session`)}
              progressing={isFetching}
            >
              <Flex alignChildrenVertical="middle" direction="horizontal" gap="x2">
                <Text>See User Sessions ({formatter1(total)})</Text>
                <Icon name="ArrowForward" />
              </Flex>
            </ProgressButton>
          </Flex>
        </Flex>

        <Flex
          alignChildrenHorizontal="between"
          direction={match('1056px') ? 'horizontal' : 'vertical'}
          gap="x4"
          margin="x5"
          reverse={!match('1056px')}
        >
          <Flex grow>
            <FiltersSelector />
          </Flex>

          <Flex direction={match('1056px') ? 'horizontal' : 'vertical'} gap="x4">
            <Flex>
              <TimeRange />
            </Flex>
          </Flex>
        </Flex>

        <Filters />
      </PageHeader>

      <PageBody>
        <Card
          margin="x16"
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerEnter.cancel}
        >
          <CardContent direction="vertical" grow padding="x6">
            <Flex alignChildrenHorizontal="between" direction="horizontal" gap="x4">
              <Flex>
                <Text ellipsis>{METRIC_NAME_BY_KEY[metric]}</Text>
                <Text config="9" ellipsis textColor="gray-5">
                  {METRIC_UNIT_NAME[METRIC_UNIT_BY_KEY[metric]]}
                </Text>
              </Flex>

              <Flex>
                <AttributeSelector metric={metric} />
              </Flex>
            </Flex>

            {METRIC_IS_COUNTER_BY_KEY[metric] ? (
              <StackedBarChart
                metrics={metricData.metrics}
                isFetched={metricData.isFetched}
                isFetching={metricData.isFetching}
                isError={metricData.isError}
                data-trace-at-id={dataAtIds.PerformanceDetailTimelineVisualisation}
                height={140}
                margin="x6"
                metric={metric}
              />
            ) : (
              <LineChart
                metrics={metricData.metrics}
                isFetched={metricData.isFetched}
                isFetching={metricData.isFetching}
                isError={metricData.isError}
                data-trace-at-id={dataAtIds.PerformanceDetailTimelineVisualisation}
                height={140}
                margin="x6"
                metric={metric}
              />
            )}
          </CardContent>
        </Card>

        <PerformanceDetailBreakdown />
      </PageBody>
    </Page>
  );
};

export default Detail;
